import { Icon } from './Icon'
import {
  canPlayInApp,
  youtubeEmbed,
  youtubeLinks,
  youtubeSandbox,
  youtubeThumb,
  type YouTubeVideo,
} from './youtubeLink'
import './youtube.css'

/** `95` → `1:35`, `3725` → `1:02:05`. */
function clock(s: number): string {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/**
 * The videos a message links to, each as its picture with a play mark — the sign that
 * the link plays here. Tapping one opens the in-app player (`onPlay`); where this app may
 * not frame it (an iPhone / Android build without the frame guard, or no player mounted
 * above), the same picture is the link to YouTube it always was.
 */
export function YouTubeCards({ text, onPlay }: { text: string; onPlay?: ((v: YouTubeVideo) => void) | null }) {
  const videos = youtubeLinks(text)
  if (!videos.length) return null
  const inApp = !!onPlay && canPlayInApp()
  return (
    <div className="yt-cards">
      {videos.map((v) => {
        const face = (
          <>
            <img className="yt-thumb" src={youtubeThumb(v.id)} alt="" loading="lazy" />
            <span className="yt-play" aria-hidden="true">
              <Icon name={inApp ? 'play_arrow' : 'open_in_new'} size={inApp ? 26 : 18} />
            </span>
            <span className="yt-tag">YouTube{v.start > 0 ? ` · ${clock(v.start)}` : ''}</span>
          </>
        )
        const cls = `yt-card${v.short ? ' short' : ''}`
        return inApp ? (
          <button key={v.id} type="button" className={cls} aria-label="Play the YouTube video" onClick={() => onPlay!(v)}>
            {face}
          </button>
        ) : (
          <a key={v.id} className={cls} href={v.url} target="_blank" rel="noopener noreferrer" aria-label="Open the video on YouTube">
            {face}
          </a>
        )
      })}
    </div>
  )
}

/**
 * THE IN-APP PLAYER — the cover panel a picture opens in (#fileView), holding YouTube's
 * own player. Its header carries the way out to YouTube itself, left of Close.
 */
export function YouTubeViewer({ video, onClose }: { video: YouTubeVideo; onClose: () => void }) {
  const framed = canPlayInApp()
  return (
    <div id="fileView" aria-label="Playing a YouTube video">
      <div className="fv-hd">
        <Icon name="play_arrow" size={15} />
        <span className="fv-name" title={video.url}>YouTube</span>
        {video.start > 0 && <span className="fv-meta">from {clock(video.start)}</span>}
        <a className="yt-out" href={video.url} target="_blank" rel="noopener noreferrer">
          <Icon name="open_in_new" size={14} />
          Open in YouTube
        </a>
        <button type="button" className="fs-icon" onClick={onClose} title="Close" aria-label="Close">
          <Icon name="close" size={15} label="Close" />
        </button>
      </div>
      <div className="fv-body yt-stage">
        {framed ? (
          <iframe
            className={`yt-frame${video.short ? ' short' : ''}`}
            src={youtubeEmbed(video)}
            title="YouTube video"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            // YouTube's player refuses to start without knowing which site embeds it.
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox={youtubeSandbox()}
          />
        ) : (
          <div className="fs-note">This version of the app cannot play videos inside it yet — open it in YouTube.</div>
        )}
      </div>
    </div>
  )
}
